import { Navigate, type RouteObject } from "react-router";

import { AppShell } from "@/shell/AppShell";
import { LibraryAssetRoute } from "@/routes/LibraryAssetRoute";
import { LibraryRoute } from "@/routes/LibraryRoute";
import { NotFoundRoute } from "@/routes/NotFoundRoute";
import {
  ProjectClinicalConfigRoute,
  ProductSettingsRedirectRoute,
} from "@/routes/ProjectConfigRoute";
import { ProjectCreateFromRoute } from "@/routes/ProjectCreateFromRoute";
import { ProjectLayoutRoute } from "@/routes/ProjectLayoutRoute";
import { ProjectOverviewRoute } from "@/routes/ProjectOverviewRoute";
import { ProjectsIndexRoute } from "@/routes/ProjectsIndexRoute";
import { UserAdministrationRoute } from "@/routes/UserAdministrationRoute";

/**
 * Route table for the Clinician Editor.
 *
 * Three rules govern it, all enforced by tests in src/invariants:
 *
 *  1. No path segment or parameter names a patient or a candidate. Project
 *     identifiers are opaque and server-issued (ADR 0001).
 *  2. The table is EXHAUSTIVE. `src/invariants/routes.test.ts` holds it against
 *     an allow-list and fails on any address that is registered without being
 *     listed there, so a new post-login surface cannot arrive unnoticed.
 *  3. Everything else is the 404 screen.
 *
 * ## #516: the canonical post-login surface, and nothing beyond it
 *
 * The ratified product reference exposes a materially smaller surface than this
 * application used to. Five addresses are gone from the table below, and they
 * are gone rather than answering with an explanation:
 *
 *   `/projects/:projectId/adoptions`, `/adoptions/*`  (#372, retired by #489)
 *   `/projects/:projectId/overview`                   (#203)
 *   `/roles`                                          (#265)
 *   `/audit`                                          (#153)
 *   `/_fixtures`                                      (the original scaffold)
 *
 * Each of the first four survived its own retirement on the reasoning that a
 * bookmarked link should land on a statement rather than a 404. That reasoning
 * is what produced four addresses the reference does not have, and #516 settles
 * it the other way: the reference answers 404 at `/projects/:id/overview`,
 * `/projects/:id/adoptions` and `/roles`, with `/projects/:id/config/bg-sets`
 * rendering beside them as the control that makes those 404s its real answer
 * rather than an artifact of static hosting.
 *
 * ## The project root IS the Overview
 *
 * `/projects/:projectId` renders lifecycle and publication detail, which is
 * what the reference puts there and what the Overview tab's own href points at.
 * This REVERSES #203's index (`<ProjectClinicalConfigRoute landing />`), which
 * landed a Project on its clinical content and kept lifecycle detail at
 * `overview`. `ProjectLayoutRoute.tsx` deferred the reversal to #448; #448 was
 * scoped to annotating the reference spec and never covered this application,
 * so the work has no other home and lands here.
 *
 * The root stays a real address that answers IN PLACE — no redirect — so the
 * deployed golden-preview check still finds a project deep link where it left
 * it. What changed is what answers there, not whether the address survives.
 *
 * ## The two legacy product-settings addresses REDIRECT to the PROJECT ROOT
 *
 * #153 moved Product Settings to the Tenant Manifest domain and left an
 * explanatory removed state behind. #516's Definition of Done replaces that
 * state with the reference's redirect. The target is the project ROOT, which is
 * what the canonical authority was measured doing at every one of these
 * addresses: the ticket's own "redirect to BG sets" wording loses to the
 * "exactly as canonical" in the same sentence, per its Scope and its first
 * Definition-of-Done bullet. Evidence and reasoning sit on
 * `ProductSettingsRedirectRoute` in `routes/ProjectConfigRoute.tsx`.
 *
 * The bare `config` redirect below is a different rule and still resolves to
 * `config/bg-sets`: a section surface reached without a section has a default
 * section, not a missing address.
 *
 * They are registered OUTSIDE `ProjectLayoutRoute` on purpose: a redirect has
 * nothing to render, so making it a child would make it wait on the layout's
 * project query before it could move — a fetch to reach an address that never
 * needed one.
 */
export const routes: RouteObject[] = [
  {
    path: "/",
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to="/projects" replace /> },

      { path: "projects", element: <ProjectsIndexRoute /> },
      {
        path: "projects/:projectId",
        element: <ProjectLayoutRoute />,
        children: [
          { index: true, element: <ProjectOverviewRoute /> },
          { path: "config", element: <ProjectClinicalConfigRoute /> },
          { path: "config/:section", element: <ProjectClinicalConfigRoute /> },
          // #518: `/preview` and `/submit` are ROUTE-BACKED MODALS over the
          // Overview — the address answers with the Overview plus the named
          // canonical dialog, and closing the dialog navigates to the project
          // root. The element is `ProjectOverviewRoute` ON PURPOSE: the same
          // component type at the same route position means React reconciles
          // the page in place across open/close, keeping the opener's DOM
          // node alive for the dialog's focus return. Neither address
          // declares a patient or candidate parameter, and never will —
          // those identities ride in state and request bodies, never in the
          // address (ADR 0001, asserted by src/invariants/routes.test.ts).
          { path: "preview", element: <ProjectOverviewRoute modal="preview" /> },
          { path: "submit", element: <ProjectOverviewRoute modal="submit" /> },
          // #518: the canonical one-time-copy surface, a full page in the
          // project frame exactly as the canonical authority renders it.
          { path: "create-from", element: <ProjectCreateFromRoute /> },
        ],
      },

      // #153 removed Product Settings from this application; #516 turns the
      // legacy addresses into the reference's redirect. `product-settings/*`
      // catches anything deeper that was ever linked.
      {
        path: "projects/:projectId/product-settings",
        element: <ProductSettingsRedirectRoute />,
      },
      {
        path: "projects/:projectId/product-settings/*",
        element: <ProductSettingsRedirectRoute />,
      },

      { path: "library", element: <Navigate to="/library/pathways" replace /> },
      { path: "library/:section", element: <LibraryRoute /> },
      // Asset ids are opaque and server-issued, like project ids (ADR 0001).
      { path: "library/:section/:assetId", element: <LibraryAssetRoute /> },

      // User administration: the third primary destination, beneath Clinical
      // library. It is the successor of the `/roles` surface #516 deleted —
      // deleted because the canonical reference had no such address at the
      // time, not because the model changed. The Editor-mastered model (#130,
      // #264) is unchanged: this address is a UI over `/v1/role-assignments`
      // and the atomic `/v1/subject-roles` write, gated by the SERVICE on
      // `authz.manage`. The nav entry hides on the session's capability; the
      // route itself answers the service's 403 as a denied state, so a typed
      // address is refused by the server rather than by the absence of a link.
      // This is the deliberate edit `src/invariants/routes.test.ts` exists to
      // force, made in the same diff as the allow-list entry.
      { path: "user-administration", element: <UserAdministrationRoute /> },

      { path: "*", element: <NotFoundRoute /> },
    ],
  },
];
